import type {
  ClientCustomStatusFilterValueItem,
  TagsFilterValue,
} from 'src/shared/Filters/_types';
import type { Identifier } from 'src/types/utilityTypes';

/**
 * Normalize a `tags` filter value from `useListSearchParams.filterParams` into
 * a plain `string[]` of tag names.
 *
 * `TagsFilter` writes two different shapes into the URL depending on its
 * `showMatchAll` prop:
 * - `showMatchAll={false}` (the default for newer pages) -> a comma-separated
 *   string, for example `"Supplements,Apparel"`
 * - `showMatchAll={true}` -> `{ values: string[], matchAll?: boolean }`
 *
 * Call this before passing the value to a fetcher so pages do not silently
 * break when the underlying filter shape changes.
 */
export const normalizeTagFilterValue = (
  value: TagsFilterValue | string | null | undefined
): string[] => {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value?.values)) {
    return value.values;
  }

  return [];
};

/** Serialize client status filters into the tuples expected by the API. */
export const serializeCustomStatusFilter = (
  values: ClientCustomStatusFilterValueItem[] | null | undefined,
  trainerId?: Identifier
): string[] => {
  if (!values?.length) return [];

  const results: string[] = [];

  for (const item of values) {
    if (item.value?.startsWith('visit_')) {
      if (item.visitValue?.operator) {
        results.push(
          `${item.value || ''},${item.visitValue.operator},${item.visitValue.timeframeDays || ''},${item.visitValue.value ?? ''},${trainerId || ''}`
        );
      }
    } else {
      results.push(
        `${item.value || ''},${item.number || ''},${item.start || ''},${item.end || ''},${trainerId || ''}`
      );
    }
  }

  return results;
};
